(() => {
  const DEFAULT_PAGE_SIZE = 10

  const normalize = (value) => String(value || '').trim()
  const normalizeSearch = (value) => normalize(value).toLowerCase()

  const debounce = (fn, waitMs) => {
    let timeoutId
    return (...args) => {
      window.clearTimeout(timeoutId)
      timeoutId = window.setTimeout(() => fn(...args), waitMs)
    }
  }

  const haversineKm = (a, b) => {
    const toRad = (deg) => (deg * Math.PI) / 180
    const R = 6371
    const dLat = toRad(b.lat - a.lat)
    const dLng = toRad(b.lng - a.lng)
    const lat1 = toRad(a.lat)
    const lat2 = toRad(b.lat)
    const sinDLat = Math.sin(dLat / 2)
    const sinDLng = Math.sin(dLng / 2)
    const x = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)))
  }

  const asNumberOrNull = (value) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }

  class UltaStoreLocator extends HTMLElement {
    constructor() {
      super()

      this.uid = normalize(this.getAttribute('id')) || `ulta-locator-${Math.random().toString(36).slice(2, 10)}`

      this.state = {
        allStores: [],
        filteredStores: [],
        clusterFilterIds: null,
        query: '',
        stateFilter: '',
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        userLocation: null,
        selectedStoreId: null,
        enableMap: true,
        enableGeolocation: true,
        jsonUrl: null,
        defaultCountry: 'US',
        mapApiKey: null,
        websiteUrl: null,
        mapReady: false,
        mapInstance: null,
        mapMarkers: [],
        mapClusterer: null,
        mapInfoWindow: null,
        isLoading: true,
        loadError: null,
      }

      this.selectors = {
        root: '[data-ulta-locator-root]',
        queryInput: '[data-ulta-query]',
        stateSelect: '[data-ulta-state]',
        locateBtn: '[data-ulta-locate]',
        clearBtn: '[data-ulta-clear]',
        status: '[data-ulta-status]',
        results: '[data-ulta-results]',
        pager: '[data-ulta-pager]',
        pagerPrev: '[data-ulta-prev]',
        pagerNext: '[data-ulta-next]',
        pagerInfo: '[data-ulta-page-info]',
        mapWrap: '[data-ulta-map]',
        mapPlaceholder: '[data-ulta-map-placeholder]',
        mapFrame: '[data-ulta-map-frame]',
        mapCanvas: '[data-ulta-map-canvas]',
      }

      this.onQueryInput = debounce(() => {
        const input = this.queryInputEl
        this.setQuery(input ? input.value : '')
      }, 160)

      this.onStateChange = () => {
        const select = this.stateSelectEl
        this.setStateFilter(select ? select.value : '')
      }

      this.onPrev = () => this.setPage(this.state.page - 1)
      this.onNext = () => this.setPage(this.state.page + 1)
      this.onLocate = () => this.requestUserLocation()
      this.onClear = () => this.clearFilters()

      this.onResultsClick = (event) => {
        const row = event.target.closest('[data-ulta-store-id]')
        if (!row) return
        const id = Number(row.getAttribute('data-ulta-store-id'))
        if (!Number.isFinite(id)) return
        this.selectStore(id)
      }

    }

    connectedCallback() {
      if (this.hasAttribute('data-initialized')) return
      this.setAttribute('data-initialized', 'true')

      this.state.pageSize = Math.max(
        5,
        Math.min(50, Number(this.getAttribute('data-page-size')) || DEFAULT_PAGE_SIZE),
      )
      this.state.enableMap = String(this.getAttribute('data-enable-map')) !== 'false'
      this.state.enableGeolocation = String(this.getAttribute('data-enable-geolocation')) !== 'false'
      const jsonUrlRaw = this.getAttribute('data-json-url')
      this.state.jsonUrl = jsonUrlRaw ? (new URL(jsonUrlRaw, window.location.href)).href : null
      this.state.defaultCountry = normalize(this.getAttribute('data-default-country')) || 'US'
      this.state.mapApiKey = normalize(this.getAttribute('data-map-api-key'))
      this.state.websiteUrl = normalize(this.getAttribute('data-website-url')) || 'https://bit.ly/csc-ulta'

      this.renderShell()
      this.cacheDom()
      this.bindEvents()
      this.loadData()
      this.initMapLazyLoad()
    }

    disconnectedCallback() {
      this.unbindEvents()
      if (this.mapObserver) this.mapObserver.disconnect()
    }

    renderShell() {
      this.innerHTML = `
        <div class="ulta-store-locator" data-ulta-locator-root>
          <div class="ulta-store-locator__controls">
            <div class="ulta-store-locator__search">
              <label class="ulta-store-locator__label" for="ulta-locator-query-${this.uid}">Search</label>
              <input
                id="ulta-locator-query-${this.uid}"
                class="ulta-store-locator__input"
                type="search"
                inputmode="search"
                autocomplete="off"
                placeholder="City, state, ZIP"
                data-ulta-query
              />
            </div>
            <div class="ulta-store-locator__filters">
              <label class="ulta-store-locator__label" for="ulta-locator-state-${this.uid}">State</label>
              <select id="ulta-locator-state-${this.uid}" class="ulta-store-locator__select" data-ulta-state>
                <option value="">All</option>
              </select>
            </div>
            <div class="ulta-store-locator__actions">
              <button class="ulta-store-locator__btn" type="button" data-ulta-locate ${this.state.enableGeolocation ? '' : 'disabled'}>
                Use my location
              </button>
              <button class="ulta-store-locator__btn ulta-store-locator__btn--ghost" type="button" data-ulta-clear>
                Clear
              </button>
            </div>
          </div>

          <div class="ulta-store-locator__status" data-ulta-status></div>

          <div class="ulta-store-locator__layout ${this.state.enableMap ? '' : 'ulta-store-locator__layout--list-only'}">
            <div class="ulta-store-locator__list">
              <div class="ulta-store-locator__results" data-ulta-results aria-live="polite"></div>
              <div class="ulta-store-locator__pager" data-ulta-pager>
                <button class="ulta-store-locator__pager-btn" type="button" data-ulta-prev>Prev</button>
                <div class="ulta-store-locator__pager-info" data-ulta-page-info></div>
                <button class="ulta-store-locator__pager-btn" type="button" data-ulta-next>Next</button>
              </div>
            </div>

            <div class="ulta-store-locator__map-sticky-wrap">
            <div class="ulta-store-locator__map" data-ulta-map ${this.state.enableMap ? '' : 'hidden'}>
              <div class="ulta-store-locator__map-canvas" data-ulta-map-canvas hidden></div>
              <div class="ulta-store-locator__map-placeholder" data-ulta-map-placeholder>
                Select a store to view it on the map
              </div>
              <iframe
                class="ulta-store-locator__map-frame"
                data-ulta-map-frame
                title="Store map"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                allowfullscreen
              ></iframe>
            </div>
            </div>
          </div>
        </div>
      `
    }

    cacheDom() {
      const root = this.querySelector(this.selectors.root)
      this.rootEl = root
      this.queryInputEl = root.querySelector(this.selectors.queryInput)
      this.stateSelectEl = root.querySelector(this.selectors.stateSelect)
      this.locateBtnEl = root.querySelector(this.selectors.locateBtn)
      this.clearBtnEl = root.querySelector(this.selectors.clearBtn)
      this.statusEl = root.querySelector(this.selectors.status)
      this.resultsEl = root.querySelector(this.selectors.results)
      this.pagerEl = root.querySelector(this.selectors.pager)
      this.prevEl = root.querySelector(this.selectors.pagerPrev)
      this.nextEl = root.querySelector(this.selectors.pagerNext)
      this.pagerInfoEl = root.querySelector(this.selectors.pagerInfo)
      this.mapWrapEl = root.querySelector(this.selectors.mapWrap)
      this.mapPlaceholderEl = root.querySelector(this.selectors.mapPlaceholder)
      this.mapFrameEl = root.querySelector(this.selectors.mapFrame)
    }

    bindEvents() {
      if (this.queryInputEl) this.queryInputEl.addEventListener('input', this.onQueryInput)
      if (this.stateSelectEl) this.stateSelectEl.addEventListener('change', this.onStateChange)
      if (this.prevEl) this.prevEl.addEventListener('click', this.onPrev)
      if (this.nextEl) this.nextEl.addEventListener('click', this.onNext)
      if (this.locateBtnEl) this.locateBtnEl.addEventListener('click', this.onLocate)
      if (this.clearBtnEl) this.clearBtnEl.addEventListener('click', this.onClear)
      if (this.resultsEl) this.resultsEl.addEventListener('click', this.onResultsClick)
    }

    unbindEvents() {
      if (this.queryInputEl) this.queryInputEl.removeEventListener('input', this.onQueryInput)
      if (this.stateSelectEl) this.stateSelectEl.removeEventListener('change', this.onStateChange)
      if (this.prevEl) this.prevEl.removeEventListener('click', this.onPrev)
      if (this.nextEl) this.nextEl.removeEventListener('click', this.onNext)
      if (this.locateBtnEl) this.locateBtnEl.removeEventListener('click', this.onLocate)
      if (this.clearBtnEl) this.clearBtnEl.removeEventListener('click', this.onClear)
      if (this.resultsEl) this.resultsEl.removeEventListener('click', this.onResultsClick)
    }

    async loadData() {
      this.setLoading(true)
      this.setStatus('Loading stores…')
      this.setError(null)

      try {
        if (!this.state.jsonUrl) throw new Error('Missing store data URL')
        const controller = new AbortController()
        const timeoutId = window.setTimeout(() => controller.abort(), 15000)
        const response = await fetch(this.state.jsonUrl, { credentials: 'same-origin', signal: controller.signal })
        window.clearTimeout(timeoutId)
        if (!response.ok) throw new Error(`Failed to load stores (${response.status})`)

        const data = await response.json()
        const stores = Array.isArray(data) ? data : []
        this.state.allStores = stores.map((s) => this.normalizeStore(s)).filter((s) => s.name || s.address)
        this.populateStateOptions()
        this.setLoading(false)
        this.setStatus('')
        this.applyFilters()
        requestAnimationFrame(() => {
          const root = this.querySelector(this.selectors.root)
          if (root) {
            this.resultsEl = root.querySelector(this.selectors.results)
            this.pagerEl = root.querySelector(this.selectors.pager)
            this.prevEl = root.querySelector(this.selectors.pagerPrev)
            this.nextEl = root.querySelector(this.selectors.pagerNext)
            this.pagerInfoEl = root.querySelector(this.selectors.pagerInfo)
          }
          this.renderResults()
        })
      } catch (err) {
        this.setLoading(false)
        const msg = err && err.message ? err.message : 'Failed to load stores'
        this.setError(err && err.name === 'AbortError' ? 'Request timed out. Check your connection.' : msg)
        this.setStatus('Could not load stores')
        this.renderResults()
      }
    }

    normalizeStore(store) {
      const id = Number(store.id)
      const city = normalize(store.city)
      const state = normalize(store.state).toUpperCase()
      const postalCode = normalize(store.postalCode)
      const street = normalize(store.street)
      const name = normalize(store.name)
      const address = normalize(store.address) || [street, city, state, postalCode].filter(Boolean).join(', ')
      const latitude = asNumberOrNull(store.latitude)
      const longitude = asNumberOrNull(store.longitude)
      const key = normalizeSearch([name, street, city, state, postalCode].filter(Boolean).join(' '))

      return {
        id: Number.isFinite(id) ? id : null,
        name,
        street,
        city,
        state,
        postalCode,
        address,
        latitude,
        longitude,
        _key: key,
      }
    }

    populateStateOptions() {
      if (!this.stateSelectEl) return

      const states = new Set()
      this.state.allStores.forEach((s) => {
        if (s.state) states.add(s.state)
      })

      const sorted = Array.from(states).sort((a, b) => a.localeCompare(b))
      const current = this.stateSelectEl.value

      this.stateSelectEl.innerHTML = ['<option value="">All</option>']
        .concat(sorted.map((s) => `<option value="${s}">${s}</option>`))
        .join('')

      if (current) this.stateSelectEl.value = current
    }

    setQuery(value) {
      this.state.query = normalize(value)
      this.state.page = 1
      this.applyFilters()
    }

    setStateFilter(value) {
      this.state.stateFilter = normalize(value).toUpperCase()
      this.state.page = 1
      this.applyFilters()
    }

    clearFilters() {
      this.state.query = ''
      this.state.stateFilter = ''
      this.state.clusterFilterIds = null
      this.state.userLocation = null
      this.state.page = 1
      this.state.selectedStoreId = null

      if (this.queryInputEl) this.queryInputEl.value = ''
      if (this.stateSelectEl) this.stateSelectEl.value = ''

      this.applyFilters()
      this.setStatus('')
      if (this.state.mapInfoWindow) this.state.mapInfoWindow.close()
      this.updateMap(null)
    }

    getDisplayStores() {
      if (this.state.clusterFilterIds && this.state.clusterFilterIds.length > 0) {
        const set = new Set(this.state.clusterFilterIds)
        return this.state.filteredStores.filter((s) => set.has(s.id))
      }
      return this.state.filteredStores
    }

    setPage(nextPage) {
      const totalPages = this.getTotalPages()
      const clamped = Math.max(1, Math.min(totalPages, nextPage))
      if (clamped === this.state.page) return
      this.state.page = clamped
      this.renderResults()
      const section = this.closest('.ulta-store-locator-section')
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    getTotalPages() {
      const display = this.getDisplayStores()
      return Math.max(1, Math.ceil(display.length / this.state.pageSize))
    }

    applyFilters() {
      this.state.clusterFilterIds = null
      const query = normalizeSearch(this.state.query)
      const stateFilter = this.state.stateFilter

      let filtered = this.state.allStores

      if (stateFilter) filtered = filtered.filter((s) => s.state === stateFilter)
      if (query) filtered = filtered.filter((s) => s._key.includes(query))

      const hasUser = this.state.userLocation && Number.isFinite(this.state.userLocation.lat) && Number.isFinite(this.state.userLocation.lng)

      if (hasUser) {
        filtered = filtered
          .map((s) => {
            if (Number.isFinite(s.latitude) && Number.isFinite(s.longitude)) {
              const km = haversineKm(this.state.userLocation, { lat: s.latitude, lng: s.longitude })
              return { ...s, _distanceKm: km }
            }
            return { ...s, _distanceKm: null }
          })
          .sort((a, b) => {
            const ad = a._distanceKm
            const bd = b._distanceKm
            if (ad == null && bd == null) return a._key.localeCompare(b._key)
            if (ad == null) return 1
            if (bd == null) return -1
            return ad - bd
          })
      } else {
        filtered = filtered.slice().sort((a, b) => {
          const stateA = a.state || ''
          const stateB = b.state || ''
          if (stateA !== stateB) return stateA.localeCompare(stateB)
          return a._key.localeCompare(b._key)
        })
      }

      this.state.filteredStores = filtered
      this.state.page = 1
      this.renderResults()
      if (this.state.mapInstance && this.state.mapApiKey) this.updateMapMarkers()
    }

    setLoading(isLoading) {
      this.state.isLoading = Boolean(isLoading)
    }

    setError(message) {
      this.state.loadError = message
    }

    setStatus(message) {
      if (!this.statusEl) return
      this.statusEl.textContent = message || ''
    }

    formatDistance(km) {
      if (!Number.isFinite(km)) return ''
      const miles = km * 0.621371
      if (miles < 10) return `${miles.toFixed(1)} mi`
      return `${Math.round(miles)} mi`
    }

    getCurrentPageStores() {
      const display = this.getDisplayStores()
      const start = (this.state.page - 1) * this.state.pageSize
      return display.slice(start, start + this.state.pageSize)
    }

    renderResults() {
      const root = this.querySelector(this.selectors.root)
      const resultsEl = root ? root.querySelector(this.selectors.results) : this.resultsEl
      const pagerEl = root ? root.querySelector(this.selectors.pager) : this.pagerEl
      if (!resultsEl || !pagerEl) return

      if (this.state.loadError) {
        resultsEl.innerHTML = `<div class="ulta-store-locator__empty">${this.escapeHtml(this.state.loadError)}</div>`
        pagerEl.hidden = true
        return
      }

      if (this.state.isLoading) {
        resultsEl.innerHTML = `<div class="ulta-store-locator__empty">Loading…</div>`
        pagerEl.hidden = true
        return
      }

      const displayStores = this.getDisplayStores()
      const total = displayStores.length
      if (!total) {
        resultsEl.innerHTML = `<div class="ulta-store-locator__empty">No stores match your search.</div>`
        pagerEl.hidden = true
        return
      }

      const pageStores = this.getCurrentPageStores()
      const startIdx = (this.state.page - 1) * this.state.pageSize + 1
      const endIdx = startIdx + pageStores.length - 1
      const totalPages = this.getTotalPages()

      resultsEl.innerHTML = pageStores
        .map((s) => {
          const isSelected = this.state.selectedStoreId === s.id
          const distance = this.formatDistance(s._distanceKm)
          const directionsUrl = this.buildDirectionsUrl(s)
          const mapUrl = this.buildMapUrl(s)
          const websiteUrl = this.state.websiteUrl || ''
          const websiteLink = websiteUrl ? `<a class="ulta-store-locator__link" href="${this.escapeAttr(websiteUrl)}" target="_blank" rel="noopener">Website</a>` : ''
          return `
            <div class="ulta-store-locator__card ${isSelected ? 'is-selected' : ''}" data-ulta-store-id="${s.id}">
              <div class="ulta-store-locator__card-main">
                <a class="ulta-store-locator__card-title" href="${this.escapeAttr(websiteUrl || '#')}" target="_blank" rel="noopener">${this.escapeHtml(s.name || 'Ulta Beauty')}</a>
                <div class="ulta-store-locator__card-address">${this.escapeHtml(s.address)}</div>
              </div>
              <div class="ulta-store-locator__card-meta">
                ${distance ? `<div class="ulta-store-locator__distance">${this.escapeHtml(distance)}</div>` : ''}
                <div class="ulta-store-locator__links">
                  <a class="ulta-store-locator__link" href="${this.escapeAttr(mapUrl)}" target="_blank" rel="noopener">Map</a>
                  <a class="ulta-store-locator__link" href="${this.escapeAttr(directionsUrl)}" target="_blank" rel="noopener">Directions</a>
                  ${websiteLink}
                </div>
              </div>
            </div>
          `
        })
        .join('')

      const prevEl = root ? root.querySelector(this.selectors.pagerPrev) : this.prevEl
      const nextEl = root ? root.querySelector(this.selectors.pagerNext) : this.nextEl
      const pagerInfoEl = root ? root.querySelector(this.selectors.pagerInfo) : this.pagerInfoEl
      pagerEl.hidden = totalPages <= 1
      if (prevEl) prevEl.disabled = this.state.page <= 1
      if (nextEl) nextEl.disabled = this.state.page >= totalPages
      if (pagerInfoEl) pagerInfoEl.textContent = `Showing ${startIdx}-${endIdx} of ${total}`

      if (!this._skipMapUpdate) {
        if (!this.state.selectedStoreId && pageStores[0] && this.state.enableMap) {
          this.selectStore(pageStores[0].id, { silent: true })
        } else if (this.state.enableMap) {
          const selected = this.state.filteredStores.find((s) => s.id === this.state.selectedStoreId)
          if (selected) this.updateMap(selected)
        }
      }
    }

    escapeHtml(value) {
      return String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;')
    }

    escapeAttr(value) {
      return this.escapeHtml(value).replaceAll('`', '&#096;')
    }

    buildMapUrl(store) {
      const q = encodeURIComponent(store.address || '')
      return `https://www.google.com/maps?q=${q}`
    }

    buildDirectionsUrl(store) {
      const q = encodeURIComponent(store.address || '')
      return `https://www.google.com/maps/dir/?api=1&destination=${q}&destination_country=${encodeURIComponent(this.state.defaultCountry)}`
    }

    buildEmbedUrl(store) {
      const q = encodeURIComponent(store.address || '')
      return `https://www.google.com/maps?q=${q}&output=embed`
    }

    getDefaultMapEmbedUrl() {
      return 'https://www.google.com/maps?q=37.0902,-95.7129&z=4&output=embed'
    }

    selectStore(storeId, options = {}) {
      const id = Number(storeId)
      if (!Number.isFinite(id)) return
      this.state.selectedStoreId = id
      this._skipMapUpdate = true
      if (!options.silent) this.renderResults()
      this._skipMapUpdate = false
      const store = this.state.filteredStores.find((s) => s.id === id) || null
      if (store && this.state.mapInstance && this.state.mapApiKey) {
        if (Number.isFinite(store.latitude) && Number.isFinite(store.longitude)) {
          this.state.mapInstance.panTo({ lat: store.latitude, lng: store.longitude })
          const marker = this.state.mapMarkers.find((m) => m.storeId === store.id)
          if (marker) this.openInfoWindow(store, marker)
        }
      } else {
        this.updateMap(store)
      }
    }

    initMapLazyLoad() {
      if (!this.state.enableMap || !this.mapWrapEl) return

      if (!('IntersectionObserver' in window)) {
        this.state.mapReady = true
        if (!this.state.mapApiKey) this.updateMapIframe(null)
        return
      }

      this.mapObserver = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some((e) => e.isIntersecting)
          if (!isVisible) return
          this.state.mapReady = true
          this.mapObserver.disconnect()
          if (this.state.mapApiKey) {
            this.loadMapWithClustering()
          } else {
            this.updateMapIframe(this.state.filteredStores.find((s) => s.id === this.state.selectedStoreId) || null)
          }
        },
        { rootMargin: '200px 0px' },
      )

      this.mapObserver.observe(this.mapWrapEl)
    }

    loadMapWithClustering() {
      const canvas = this.querySelector(this.selectors.mapCanvas)
      if (!canvas) return
      if (this.state.mapInstance) return

      const key = this.state.mapApiKey
      const self = this
      const doInit = () => {
        if (self.state.mapInstance) return
        self.loadMarkerClusterer(() => {
          self.initMapCanvas(canvas)
        })
      }

      if (window.google?.maps?.Map) {
        doInit()
        return
      }

      if (window.__ultaMapInitScheduled) return
      window.__ultaMapInitScheduled = true
      const callbackName = 'ultaMapLocatorInit'
      window[callbackName] = () => {
        window.__ultaMapInitScheduled = false
        if (window.ultaMapLocatorInitDone) return
        window.ultaMapLocatorInitDone = true
        doInit()
      }

      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&callback=${callbackName}`
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }

    initMapCanvas(canvasEl) {
      if (!window.google?.maps?.Map || !canvasEl) return
      if (this.state.mapInstance) return

      const map = new google.maps.Map(canvasEl, {
        center: { lat: 37.09, lng: -95.71 },
        zoom: 4,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: false,
        rotateControl: false,
        scaleControl: false,
        keyboardShortcuts: false,
        clickableIcons: false,
      })

      this.state.mapInstance = map
      this.state.mapInfoWindow = new google.maps.InfoWindow()
      this.mapPlaceholderEl.hidden = true
      if (this.mapFrameEl) this.mapFrameEl.hidden = true
      canvasEl.hidden = false
      canvasEl.style.display = 'block'

      this.updateMapMarkers()
      this.startGeocodeMissingCoords()
    }

    startGeocodeMissingCoords() {
      if (!window.google?.maps?.Geocoder || !this.state.mapInstance) return
      const needCoords = this.state.allStores.filter(
        (s) => !Number.isFinite(s.latitude) || !Number.isFinite(s.longitude),
      )
      if (!needCoords.length) return
      const geocoder = new google.maps.Geocoder()
      const GEOCODE_DELAY_MS = 280
      const self = this
      let index = 0
      function next() {
        if (index >= needCoords.length) return
        const store = needCoords[index]
        index += 1
        const address = (store.address || `${store.street}, ${store.city}, ${store.state} ${store.postalCode}`).trim()
        if (!address) {
          setTimeout(next, 50)
          return
        }
        geocoder.geocode({ address: address + ', USA' }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location
            store.latitude = loc.lat()
            store.longitude = loc.lng()
            self.updateMapMarkers()
          }
          setTimeout(next, GEOCODE_DELAY_MS)
        })
      }
      setTimeout(next, 0)
    }

    updateMapMarkers() {
      const map = this.state.mapInstance
      if (!map || !window.google?.maps) return

      const storesWithCoords = this.state.filteredStores.filter(
        (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude),
      )

      if (this.state.mapClusterer) {
        this.state.mapClusterer.clearMarkers()
        this.state.mapClusterer = null
      }
      if (this.state.mapMarkers.length) {
        this.state.mapMarkers.forEach((m) => m.setMap(null))
        this.state.mapMarkers = []
      }

      if (!storesWithCoords.length) return

      const Clusterer = window.MarkerClusterer || window.markerClusterer?.MarkerClusterer
      const useClusterer = storesWithCoords.length > 1 && Clusterer

      const bounds = new google.maps.LatLngBounds()
      const markers = storesWithCoords.map((store) => {
        const marker = new google.maps.Marker({
          position: { lat: store.latitude, lng: store.longitude },
          map: useClusterer ? null : map,
          title: store.name || store.address,
        })
        marker.storeId = store.id
        marker.addListener('click', () => {
          this.state.clusterFilterIds = null
          this.selectStore(store.id)
        })
        bounds.extend(marker.getPosition())
        return marker
      })

      this.state.mapMarkers = markers

      if (markers.length > 0) {
        if (markers.length === 1) {
          map.setCenter(markers[0].getPosition())
          map.setZoom(10)
        } else {
          const listener = google.maps.event.addListener(map, 'idle', () => {
            google.maps.event.removeListener(listener)
            if (map.getZoom() > 12) map.setZoom(12)
          })
          map.fitBounds(bounds, { top: 40, right: 40, bottom: 40, left: 40 })
        }
      }

      if (useClusterer) {
        const onClusterClick = (event, cluster) => {
          const clusterMarkers = (cluster && cluster.markers) ? cluster.markers : []
          const ids = clusterMarkers.map((m) => m.storeId).filter(Boolean)
          if (!ids.length) return

          const clusterBounds = new google.maps.LatLngBounds()
          clusterMarkers.forEach((m) => {
            const pos = m.getPosition ? m.getPosition() : m.position
            if (pos) clusterBounds.extend(pos)
          })
          const clusterListener = google.maps.event.addListener(this.state.mapInstance, 'idle', () => {
            google.maps.event.removeListener(clusterListener)
            if (this.state.mapInstance.getZoom() > 12) this.state.mapInstance.setZoom(12)
          })
          this.state.mapInstance.fitBounds(clusterBounds, { top: 40, right: 40, bottom: 40, left: 40 })

          this.state.clusterFilterIds = ids
          this.state.page = 1
          this.state.selectedStoreId = null
          this._skipMapUpdate = true
          this.renderResults()
          this._skipMapUpdate = false
        }
        try {
          this.state.mapClusterer = new Clusterer({ map, markers, onClusterClick })
        } catch (_) {
          try {
            this.state.mapClusterer = new Clusterer(map, markers, { onClusterClick })
          } catch (e2) {
            markers.forEach((m) => m.setMap(map))
          }
        }
      } else if (markers.length > 1) {
        markers.forEach((m) => m.setMap(map))
      }
    }

    loadMarkerClusterer(callback) {
      const setClusterer = () => {
        if (window.markerClusterer?.MarkerClusterer && !window.MarkerClusterer) {
          window.MarkerClusterer = window.markerClusterer.MarkerClusterer
        }
      }
      if (window.MarkerClusterer || window.markerClusterer?.MarkerClusterer) {
        setClusterer()
        callback()
        return
      }
      const urls = [
        'https://cdn.jsdelivr.net/npm/@googlemaps/js-markerclusterer/dist/index.umd.js',
        'https://unpkg.com/@googlemaps/js-markerclusterer/dist/index.umd.js',
        'https://cdn.jsdelivr.net/npm/@googlemaps/markerclusterer@2.5.2/dist/index.umd.js',
      ]
      let idx = 0
      const load = () => {
        if (window.MarkerClusterer || window.markerClusterer?.MarkerClusterer) {
          setClusterer()
          callback()
          return
        }
        if (idx >= urls.length) {
          callback()
          return
        }
        const script = document.createElement('script')
        script.src = urls[idx]
        idx += 1
        script.onload = () => {
          setClusterer()
          callback()
        }
        script.onerror = load
        document.head.appendChild(script)
      }
      load()
    }

    updateMap(store) {
      if (!this.state.enableMap || !this.mapWrapEl) return
      if (this.state.mapApiKey && this.state.mapInstance) {
        this.updateMapMarkers()
        return
      }
      this.updateMapIframe(store)
    }

    openInfoWindow(store, marker) {
      const iw = this.state.mapInfoWindow
      if (!iw || !this.state.mapInstance) return
      const directionsUrl = this.buildDirectionsUrl(store)
      const distance = store._distanceKm ? this.formatDistance(store._distanceKm) : ''
      const websiteUrl = this.state.websiteUrl || ''
      const websiteLink = websiteUrl ? `<a class="ulta-store-locator__map-panel-link" href="${this.escapeAttr(websiteUrl)}" target="_blank" rel="noopener">Website</a>` : ''
      iw.setContent(`
        <div class="ulta-store-locator__map-panel">
          <div class="ulta-store-locator__map-panel-name">${this.escapeHtml(store.name || 'Ulta Beauty')}</div>
          <div class="ulta-store-locator__map-panel-address">${this.escapeHtml(store.address)}</div>
          ${distance ? `<div class="ulta-store-locator__map-panel-distance">${this.escapeHtml(distance)}</div>` : ''}
          <a class="ulta-store-locator__map-panel-link" href="${this.escapeAttr(directionsUrl)}" target="_blank" rel="noopener">Get Directions</a>
          ${websiteLink}
        </div>
      `)
      iw.open(this.state.mapInstance, marker)
    }

    updateMapIframe(store) {
      if (!this.mapFrameEl || !this.mapPlaceholderEl) return
      this.mapPlaceholderEl.hidden = true
      this.mapFrameEl.hidden = false
      const src = store ? this.buildEmbedUrl(store) : this.getDefaultMapEmbedUrl()
      if (this.mapFrameEl.getAttribute('src') !== src) this.mapFrameEl.setAttribute('src', src)
    }

    requestUserLocation() {
      if (!this.state.enableGeolocation) return
      if (!('geolocation' in navigator)) {
        this.setStatus('Geolocation is not supported in this browser.')
        return
      }

      this.setStatus('Finding your location…')

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = asNumberOrNull(pos && pos.coords ? pos.coords.latitude : null)
          const lng = asNumberOrNull(pos && pos.coords ? pos.coords.longitude : null)

          if (lat == null || lng == null) {
            this.setStatus('Could not read your location.')
            return
          }

          this.state.userLocation = { lat, lng }
          const hasCoords = this.state.allStores.some((s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude))
          this.setStatus(hasCoords ? 'Showing closest stores first.' : 'Location saved. Add store coordinates to enable distance sorting.')
          this.applyFilters()
        },
        () => {
          this.setStatus('Location permission was not granted.')
        },
        { enableHighAccuracy: false, timeout: 9000, maximumAge: 600000 },
      )
    }
  }

  if (customElements.get('ulta-store-locator') === undefined) {
    customElements.define('ulta-store-locator', UltaStoreLocator)
  }
})()

